import { useCallback, useEffect, useRef, useState } from 'react';

import { Modal } from '@/components/Modal';
import { BRAND_COLOR } from '@/lib/brand';
import { useDeleteSignature, useMySignature, useSaveSignature } from '@/pages/approval/useApproval';

/**
 * 개인 서명 그림판 (V327 · 사용자 지시 2026-09-22).
 *
 * <pre>
 *   「전자결재용 개인 서명을 받을거야. 개인서명을 미리 사용자가 작성할 수 있게 하자.
 *    그림판같은 화면을 모달에 띄워주고, 작성해서 저장하면…」
 * </pre>
 *
 * <h2>결재할 때가 아니라 <b>미리</b> 그린다</h2>
 * 결재를 누르는 순간 펜을 들게 하면 결재는 「펜을 잡아야 끝나는 일」이 된다. 여기서 한 번 그려 두고,
 * 결재 화면은 그것을 찍기만 한다.
 *
 * <h2>그림은 <b>투명 배경 PNG</b> 한 장이다</h2>
 * 종이는 캔버스 아래에 깔린 흰 상자일 뿐, 내보내는 그림에는 들어가지 않는다 — 결재란 칸 위에
 * 그대로 얹혀야 하기 때문이다(흰 사각형이 찍히면 칸의 금이 가려진다). 크기는 600×200 의 두 배로
 * 굽는다: 화면에서는 작게 보여도 <b>인쇄는 그 그림을 종이 해상도로 펴므로</b>, 화면 크기로 구우면
 * 결재란의 서명만 뭉개진다.
 *
 * <h2>획으로 들고 있는다</h2>
 * 캔버스의 픽셀만 들고 있으면 「되돌리기」가 불가능하다(지워진 픽셀은 복구할 수 없다). 그려진 것을
 * <b>획의 목록</b>으로 쥐고 매번 다시 그리면, 한 획 물리기가 목록에서 하나 빼는 일이 된다. 패드에
 * 펜으로 쓰는 사람에게 이 단추는 「지우고 처음부터」와 전혀 다른 것이다.
 */

/** 내보내는 그림의 논리 크기(px). 결재란 칸이 가로로 긴 자리라 3:1 이다. */
const PAD_W = 600;
const PAD_H = 200;
/** 배경 저장소 배율 — 인쇄에서 펴지는 만큼 미리 키워 둔다. */
const PAD_SCALE = 2;
/**
 * 획 굵기(배경 저장소 1200×400 기준) — 화면에서는 그 절반으로 보인다.
 *
 * <p>가늘게 잡으면 <b>결재란에서 사라진다</b> (사용자 지적 2026-09-22 — 「이름이랑 날짜보다 글씨가
 * 얇네」). 판은 600px 폭인데 찍히는 칸은 70px 안팎이라 그림이 8분의 1로 줄고, 1px 짜리 획은 그
 * 과정에서 회색 실선이 된다. 이름 글씨(12px 굵은체)와 <b>같은 무게</b>로 보이려면 판에서 이만큼
 * 굵어야 한다.
 */
const STROKE_W = 12;

type Point = { x: number; y: number };
type Stroke = Point[];

export function SignaturePadModal({ onClose }: { onClose: () => void }) {
  const saved = useMySignature();
  const save = useSaveSignature();
  const remove = useDeleteSignature();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  /** 깔개로 쓰는 그림 — 등록해 둔 서명이거나, 방금 불러온 파일이다. */
  const baseRef = useRef<HTMLImageElement | null>(null);
  /**
   * 그 깔개의 출처(data URL). <b>상태로 쥔다</b> — ref 에만 두면 획을 하나 그을 때마다 다시 도는
   * 불러오기 효과가 밑그림을 <b>등록된 서명으로 되돌려</b>, 불러온 파일이 사라진다.
   */
  const [baseSrc, setBaseSrc] = useState<string | null>(null);
  /** 깔개가 바뀐 횟수 — 그림이 다 실린 뒤에 다시 그리라고 알리는 종이다(ref 는 렌더를 일으키지 않는다). */
  const [baseTick, setBaseTick] = useState(0);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  /** 그리는 중인 획. 화면에는 바로 긋고, 손을 떼는 순간 목록에 들어간다. */
  const drawingRef = useRef<Stroke | null>(null);
  /** 「지우기」를 누르면 밑그림(등록해 둔 서명)도 함께 사라진다. */
  const [keepBase, setKeepBase] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  const redraw = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, PAD_W * PAD_SCALE, PAD_H * PAD_SCALE);
    if (keepBase && baseRef.current) {
      /*
        등록된 서명은 <b>획에 맞춰 잘려</b> 저장돼 있어(→ {@link trimmedPng}) 판의 3:1 과 비율이
        다르다. 판에 꽉 채워 그리면 가로로 늘어난 남의 글씨가 된다 — 비율을 지켜 가운데 앉힌다.
      */
      const img = baseRef.current;
      const fit = Math.min((PAD_W * PAD_SCALE) / img.width, (PAD_H * PAD_SCALE) / img.height);
      const w = img.width * fit;
      const h = img.height * fit;
      ctx.drawImage(img, (PAD_W * PAD_SCALE - w) / 2, (PAD_H * PAD_SCALE - h) / 2, w, h);
    }

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = STROKE_W;
    ctx.strokeStyle = BRAND_COLOR.ink;
    for (const stroke of strokes) drawStroke(ctx, stroke);
  }, [keepBase, strokes]);

  useEffect(() => redraw(), [redraw, baseTick]);

  /*
    등록해 둔 서명을 밑그림으로 깐다. 빈 판으로 열면 사람은 <b>자기 서명이 지워졌는지</b>를 알 수
    없다 — 지금 등록된 것이 무엇인지가 이 창의 첫 대답이어야 한다. 그 위에 덧그릴 수도 있고,
    「지우기」로 걷어 내고 처음부터 쓸 수도 있다.

    <p>불러온 파일이 깔린 뒤에는 이 줄이 다시 덮지 않는다 — 서버 값이 바뀔 때만 따라간다.
  */
  useEffect(() => {
    if (saved.data?.image) setBaseSrc(saved.data.image);
  }, [saved.data?.image]);

  useEffect(() => {
    if (!baseSrc) {
      baseRef.current = null;
      setBaseTick((n) => n + 1);
      return;
    }
    const img = new Image();
    img.onload = () => {
      baseRef.current = img;
      setBaseTick((n) => n + 1);
    };
    img.src = baseSrc;
  }, [baseSrc]);

  const pointAt = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    // 화면에 보이는 크기와 배경 저장소의 크기가 다르다(축소 표시 · 배율). 받은 자리를 그림의 자리로 옮긴다.
    return {
      x: ((event.clientX - rect.left) * canvas.width) / rect.width,
      y: ((event.clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    // 손가락이 캔버스 밖으로 나가도 획이 끊기지 않게 포인터를 붙잡는다.
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingRef.current = [pointAt(event)];
    setMessage(null);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = drawingRef.current;
    if (!stroke) return;
    stroke.push(pointAt(event));

    // 긋는 동안은 <b>마지막 토막만</b> 얹는다. 매 움직임마다 전체를 다시 그리면 획이 늦게 따라온다.
    const ctx = canvasRef.current?.getContext('2d');
    const from = stroke.at(-2);
    const to = stroke.at(-1);
    if (ctx && from && to) {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = STROKE_W;
      ctx.strokeStyle = BRAND_COLOR.ink;
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();
    }
  };

  const onPointerUp = () => {
    const stroke = drawingRef.current;
    drawingRef.current = null;
    if (stroke && stroke.length > 0) setStrokes((prev) => [...prev, stroke]);
  };

  /**
   * 그려 둔 서명을 <b>파일로 불러온다</b> (사용자 지시 2026-09-22 — 「서명이미지를 불러오게도
   * 가능하게 하자」). 이미 도장처럼 만들어 둔 서명 파일이 있는 사람은 그것을 쓰는 것이 맞고,
   * 마우스로 다시 그린 글씨는 본인 것이라도 본인 서명이 아니다.
   *
   * <p><b>흰 바탕은 걷어 낸다.</b> 종이에 써서 찍은 사진·스캔은 대부분 흰 배경인데, 그대로 얹으면
   * 결재란에 <b>흰 사각형</b>이 붙어 칸의 금과 이름을 덮는다. 밝은 점을 투명으로 돌려 획만 남긴다.
   *
   * <p>불러온 것도 판 위의 그림일 뿐이라, 그 위에 덧그리거나 「지우기」로 걷어 낼 수 있다 —
   * 저장은 언제나 <b>지금 판에 보이는 것</b>을 굽는다.
   */
  const loadFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        setBaseSrc(inkOnly(img));
        setKeepBase(true);
        setStrokes([]);
        setMessage(null);
      };
      img.onerror = () => setMessage('이미지를 읽지 못했습니다.');
      img.src = String(reader.result);
    };
    reader.onerror = () => setMessage('이미지를 읽지 못했습니다.');
    reader.readAsDataURL(file);
  };

  // 밑그림은 ref 에 있어 다시 그려도 렌더를 일으키지 않는다 — 「있는가」 는 그 출처로 센다.
  const hasInk = strokes.length > 0 || (keepBase && Boolean(baseSrc));

  const submit = () => {
    if (!hasInk) {
      setMessage('서명을 그려 주세요.');
      return;
    }
    const image = trimmedPng(canvasRef.current);
    if (!image) {
      setMessage('서명을 그려 주세요.');
      return;
    }
    save.mutate(image, {
      onSuccess: () => onClose(),
      onError: (error) => setMessage(error.message),
    });
  };

  return (
    <Modal
      title="개인 서명"
      onClose={onClose}
      maxWidth="560px"
      closeDisabled={save.isPending || remove.isPending}
      actions={
        <div className="flex gap-1 items-center">
          <button className="btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>
            불러오기
          </button>
          <button
            className="btn-secondary btn-sm"
            onClick={() => setStrokes((prev) => prev.slice(0, -1))}
          >
            되돌리기
          </button>
          <button
            className="btn-secondary btn-sm"
            onClick={() => {
              setStrokes([]);
              setKeepBase(false);
              setMessage(null);
            }}
          >
            지우기
          </button>
          {/*
            등록해 둔 서명을 서버에서 지운다. 「지우기」(판을 비운다)와 다른 일이라 단추를 따로 둔다 —
            그리다 만 것을 걷어 내는 일과, 결재란에 찍히는 것을 없애는 일은 되돌리는 방법이 다르다.
          */}
          <button
            className="btn-danger-lg btn-sm"
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(undefined, {
                onSuccess: () => {
                  baseRef.current = null;
                  setStrokes([]);
                  setKeepBase(false);
                },
                onError: (error) => setMessage(error.message),
              })
            }
          >
            삭제
          </button>
          <button className="btn-primary btn-sm" disabled={save.isPending} onClick={submit}>
            저장
          </button>
        </div>
      }
    >
      {/*
        종이. 캔버스 <b>아래</b>에 깔린 흰 상자라 내보내는 그림에는 들어가지 않는다. 밑줄은 이름을
        어디에 앉힐지 잡아 주는 금이고, 이것 역시 그림이 아니라 상자의 테두리다.
      */}
      <div
        style={{
          background: '#fff',
          border: '1px solid var(--border-strong)',
          borderRadius: '10px',
          padding: '10px',
          touchAction: 'none',
        }}
      >
        <div style={{ borderBottom: `1px dashed ${BRAND_COLOR.line}`, lineHeight: 0 }}>
          <canvas
            ref={canvasRef}
            width={PAD_W * PAD_SCALE}
            height={PAD_H * PAD_SCALE}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={{ width: '100%', aspectRatio: `${PAD_W} / ${PAD_H}`, touchAction: 'none', cursor: 'crosshair' }}
          />
        </div>
      </div>

      {/* 파일 고르기는 단추가 대신 누른다 — 「불러오기」 옆에 회색 상자가 하나 더 서지 않게. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(event) => {
          loadFile(event.target.files?.[0]);
          // 같은 파일을 다시 골라도 바뀐 것으로 치게 비워 둔다.
          event.target.value = '';
        }}
      />

      {message && (
        <p style={{ fontSize: '12px', color: 'var(--danger)', marginTop: '8px' }}>{message}</p>
      )}
    </Modal>
  );
}

/**
 * 불러온 그림에서 <b>획만</b> 남긴다 — 밝은 점을 투명으로 돌린다.
 *
 * <h2>왜 필요한가</h2>
 * 종이에 써서 찍은 사진·스캔은 흰 바탕이다. 그대로 결재란에 얹으면 <b>흰 사각형</b>이 칸의 금과
 * 이름을 덮어, 서명을 붙였는데 칸이 지워진 것처럼 보인다. 서명은 종이 위의 잉크지 종이가 아니다.
 *
 * <h2>어떻게 가르는가</h2>
 * 가장 밝은 쪽(235 이상)은 완전히 투명, 어두운 쪽(120 이하)은 그대로 두고, 그 사이는 <b>밝기에
 * 비례해</b> 흐리게 남긴다 — 글자 가장자리의 회색 점까지 한 칼로 자르면 획이 톱니처럼 된다.
 *
 * <p>그늘진 사진(회색 바탕)은 완전히 걷히지 않을 수 있다. 그때는 더 세게 자르는 대신 <b>다시
 * 그리는</b> 쪽이 빠르다 — 문턱을 높이면 연한 획이 함께 사라져 서명이 끊긴다.
 */
function inkOnly(img: HTMLImageElement): string {
  const canvas = document.createElement('canvas');
  canvas.width = PAD_W * PAD_SCALE;
  canvas.height = PAD_H * PAD_SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return img.src;

  // 비율을 지켜 판 가운데에 앉힌다(늘이면 남의 글씨가 된다).
  const fit = Math.min(canvas.width / img.width, canvas.height / img.height);
  const w = img.width * fit;
  const h = img.height * fit;
  ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);

  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const px = image.data;
  for (let i = 0; i < px.length; i += 4) {
    const light = (px[i]! * 299 + px[i + 1]! * 587 + px[i + 2]! * 114) / 1000;
    if (light >= 235) px[i + 3] = 0;
    else if (light > 120) px[i + 3] = Math.round(px[i + 3]! * ((235 - light) / 115));
  }
  ctx.putImageData(image, 0, 0);
  return canvas.toDataURL('image/png');
}

/**
 * 그린 것을 <b>획이 닿은 만큼만</b> 잘라 PNG 로 굽는다.
 *
 * <p>판은 600×200 인데 사람이 쓰는 이름은 대개 그 왼쪽 절반이다. 판을 통째로 저장하면 결재란의
 * 좁은 칸에서 <b>빈 여백까지 줄이느라</b> 글씨가 반으로 작아진다 — 칸이 좁은 것이 아니라 그림에
 * 든 여백이 넓은 것이다. 잘라서 저장하면 같은 칸에서 두 배로 크게 앉는다.
 *
 * <p>아무것도 안 그렸으면 null 이다(잘라 낼 것이 없다) — 부르는 쪽이 「서명을 그려 주세요」로 받는다.
 */
function trimmedPng(canvas: HTMLCanvasElement | null): string | null {
  const ctx = canvas?.getContext('2d');
  if (!canvas || !ctx) return null;

  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      // 투명하지 않은 점이 곧 획이다. 배경을 칠하지 않는 판이라 알파 하나로 가른다.
      if (data[(y * width + x) * 4 + 3]! > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;

  // 획 굵기의 절반만큼은 남긴다 — 딱 맞춰 자르면 테두리가 잘린 것처럼 보인다.
  const pad = STROKE_W;
  const x0 = Math.max(0, minX - pad);
  const y0 = Math.max(0, minY - pad);
  const w = Math.min(width, maxX + pad) - x0;
  const h = Math.min(height, maxY + pad) - y0;

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  out.getContext('2d')?.drawImage(canvas, x0, y0, w, h, 0, 0, w, h);
  return out.toDataURL('image/png');
}

function drawStroke(ctx: CanvasRenderingContext2D, stroke: Stroke) {
  const first = stroke[0];
  const last = stroke.at(-1);
  if (!first || !last) return;

  ctx.beginPath();
  if (stroke.length === 1) {
    // 점 하나(톡 찍은 것)도 획이다 — 이름의 점·획 하나가 사라지면 그것은 다른 서명이다.
    ctx.arc(first.x, first.y, STROKE_W / 2, 0, Math.PI * 2);
    ctx.fillStyle = BRAND_COLOR.ink;
    ctx.fill();
    return;
  }

  /*
    점을 곧은 선으로 잇지 않는다. 포인터는 띄엄띄엄 들어와서, 이어 붙이면 빠르게 그은 곡선이
    <b>각진 꺾은선</b>이 된다. 두 점의 가운데를 지나는 2차 곡선으로 이으면 손의 궤적에 가깝다.
  */
  ctx.moveTo(first.x, first.y);
  for (let i = 1; i < stroke.length - 1; i += 1) {
    const point = stroke[i];
    const next = stroke[i + 1];
    if (!point || !next) continue;
    ctx.quadraticCurveTo(point.x, point.y, (point.x + next.x) / 2, (point.y + next.y) / 2);
  }
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
}
